import React, { useMemo, useState } from "react";
import Layout from "../components/Layout";
import useSeo from "../hooks/useSeo";
import { useResults } from "../hooks/useApi";

const PAGE_SIZE = 50;

const PreviousResult = () => {
  const { data: previous = [] } = useResults();
  const [year, setYear] = useState("all");
  const [visible, setVisible] = useState(PAGE_SIZE);

  useSeo(
    "Morning Sunday Teer Previous Result | Shillong Morning Teer Old Chart & History",
    "Complete Morning Sunday Teer Previous Result archive — daily Shillong Morning Teer F/R and S/R history with latest results on top.",
    { breadcrumb: [ { name: "Home", path: "/" }, { name: "Previous Result", path: "/previous-result" } ] }
  );

  // available years (from dd-mm-yyyy), newest first
  const years = useMemo(() => {
    const set = new Set(previous.map((r) => r.date?.split("-")[2]).filter(Boolean));
    return Array.from(set).sort((a, b) => b.localeCompare(a));
  }, [previous]);

  const filtered = useMemo(
    () => (year === "all" ? previous : previous.filter((r) => r.date?.endsWith(year))),
    [previous, year]
  );

  const shown = filtered.slice(0, visible);
  const hasMore = filtered.length > visible;

  const selectYear = (y) => {
    setYear(y);
    setVisible(PAGE_SIZE);
  };

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-4 py-6">
        <h1 className="text-2xl font-bold text-center text-[#1f2a37] mb-2">
          Morning Sunday Teer Previous Result
        </h1>
        <p className="text-center text-xs text-gray-500 italic mb-4">
          Note: The latest result appears at the top. Browse the full archive of
          F/R and S/R outcomes below.
        </p>

        {/* Year filter (only when more than one year exists) */}
        {years.length > 1 && (
          <div className="flex flex-wrap justify-center gap-2 mb-4">
            <button
              onClick={() => selectYear("all")}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                year === "all"
                  ? "bg-[#3aa79a] text-white"
                  : "bg-gray-100 text-[#374151] hover:bg-gray-200"
              }`}
            >
              All
            </button>
            {years.map((y) => (
              <button
                key={y}
                onClick={() => selectYear(y)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  year === y
                    ? "bg-[#3aa79a] text-white"
                    : "bg-gray-100 text-[#374151] hover:bg-gray-200"
                }`}
              >
                {y}
              </button>
            ))}
          </div>
        )}

        <div className="border border-gray-300 rounded-md overflow-hidden shadow-sm">
          <table className="w-full text-center text-sm">
            <thead>
              <tr className="bg-[#3aa79a] text-white">
                <th className="py-2.5 font-semibold">City</th>
                <th className="py-2.5 font-semibold">Date</th>
                <th className="py-2.5 font-semibold">F/R</th>
                <th className="py-2.5 font-semibold">S/R</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((r, i) => (
                <tr key={r.id} className={i % 2 ? "bg-gray-50" : "bg-white"}>
                  <td className="py-2 border-t border-gray-200">SHILLONG</td>
                  <td className="py-2 border-t border-gray-200">
                    {r.date.replace(/-/g, "/")}
                  </td>
                  <td className="py-2 border-t border-gray-200 font-semibold">{r.fr}</td>
                  <td className="py-2 border-t border-gray-200 font-semibold">{r.sr}</td>
                </tr>
              ))}
              {shown.length === 0 && (
                <tr>
                  <td colSpan={4} className="py-6 text-gray-400 border-t">
                    No results available.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {hasMore && (
          <div className="text-center mt-4">
            <button
              onClick={() => setVisible((v) => v + PAGE_SIZE)}
              className="bg-[#4a7fb5] hover:bg-[#3d6c9c] text-white font-medium px-6 py-2.5 rounded-md transition-colors"
            >
              Load More ({filtered.length - visible} more)
            </button>
          </div>
        )}

        <article className="text-[#374151] leading-relaxed space-y-4 mt-8">
          <h2 className="text-lg font-bold text-[#1f2a37]">
            The Power of the Previous Result Archive
          </h2>
          <p>
            Seasoned followers understand that a single day's outcome is one
            piece of a larger puzzle. The complete historical database provides
            a bird's-eye view of how numbers evolve across days, weeks and
            months. Use these records to observe how specific Houses (tens
            digits) and Endings (units digits) behave over time.
          </p>
          <p className="text-xs text-gray-500 italic">
            Historical data, no matter how deeply analysed, cannot guarantee
            future results. Always use the previous result charts as an
            educational and analytical tool and play responsibly.
          </p>
        </article>
      </div>
    </Layout>
  );
};

export default PreviousResult;
